/* eslint-disable no-console -- simple logger */
import { isSSR } from './isSSR';

/**
 * Log messages verbosity level which can be dynamically set also on production environment.
 *
 * Please, consider using Info level for rather important checks in the app workflow
 * (like data load success, user action logs, etc.).
 *
 * Use Debug level for other less important messages. This is for the possibility
 * to have the generic app flow still checked in log without necessarily overcrowding
 * it with all the debug message logs.
 */
export let LogLevel = /*#__PURE__*/function (LogLevel) {
  LogLevel[LogLevel["None"] = 0] = "None";
  LogLevel[LogLevel["Error"] = 1] = "Error";
  LogLevel[LogLevel["Warning"] = 2] = "Warning";
  LogLevel[LogLevel["Info"] = 3] = "Info";
  LogLevel[LogLevel["Debug"] = 4] = "Debug";
  return LogLevel;
}({});
/**
 * The default log level is set to warnings, which means errors & warnings
 * are logged automatically without any need to change the settings.
 *
 * For info & debug messages to be logged, the settings have to be changed manually.
 * It can be done either in the local testing page UI, or in JS console via:
 * localStorage.setItem(`${appName} debug`, 3); // LogLevel.Info for example
 */
export const DEFAULT_LOG_LEVEL = LogLevel.Warning;

/**
 * Current log methods map. So far, the console methods are used,
 * but the methods are replaceable, so for example AppInsights logger
 * might be implemented additionally to the existing functionality.
 */
export const logMethodsMap = new Map([[LogLevel.None, (...args) => {}], [LogLevel.Error, console.error], [LogLevel.Warning, console.warn], [LogLevel.Info, console.info], [LogLevel.Debug, console.log]]);
function getTime() {
  const date = new Date();
  const minutes = `0${date.getMinutes()}`;
  const seconds = `0${date.getSeconds()}`;
  const milliseconds = `00${date.getMilliseconds()}`;
  const LAST_TWO_DIGITS = -2;
  const LAST_THREE_DIGITS = -3;
  return `${date.getHours()}:${minutes.slice(LAST_TWO_DIGITS)}:${seconds.slice(LAST_TWO_DIGITS)}.${milliseconds.slice(LAST_THREE_DIGITS)}`;
}

/**
 * @param appName Application name & logging key, also part of the log level setting storage key.
 * @returns Current logging level for the specified application.
 */
export function getLogLevel(appName) {
  let result = !isSSR() && Boolean(window.localStorage) && Number(JSON.parse(localStorage.getItem(`${appName} debug`) ?? String(DEFAULT_LOG_LEVEL)));
  if (typeof result !== 'number' || Number.isNaN(result) || result < LogLevel.None || result > LogLevel.Debug) {
    result = DEFAULT_LOG_LEVEL;
  }
  return result;
}

/**
 * Generic log method wrapper. Formats the logged output a bit.
 * Skips calling the specific log method when current application log level
 * is lower than the requested log level in this call.
 * @param appName Application name / logging key.
 * @param level Requested log level for this call.
 * @param args Log message / data.
 */
export function log(appName, level, ...args) {
  const currentLevel = getLogLevel(appName);
  if (currentLevel < level) {
    return;
  }
  const time = getTime();
  const method = logMethodsMap.get(level);
  method?.(`%c${time} %c${appName}:`, 'color: #09c', 'color: #c90;', ...args);
}

/**
 * @param appName Application name / logger key.
 * @param logLevel Log messages verbosity level.
 * @returns Logger function for the specified log level.
 */
export function getLogger(appName, logLevel) {
  return (...args) => log(appName, logLevel, ...args);
}

/**
 * @param appName Application name / logger key.
 * @returns Error logger function.
 */
export function getErrorLogger(appName) {
  return getLogger(appName, LogLevel.Error);
}

/**
 * @param appName Application name / logger key.
 * @returns Warning logger function.
 */
export function getWarningLogger(appName) {
  return getLogger(appName, LogLevel.Warning);
}

/**
 * @param appName Application name / logger key.
 * @returns Info logger function.
 */
export function getInfoLogger(appName) {
  return getLogger(appName, LogLevel.Info);
}

/**
 * @param appName Application name / logger key.
 * @returns Debug logger function.
 */
export function getDebugLogger(appName) {
  return getLogger(appName, LogLevel.Debug);
}

/**
 * @param appName Application name / logger key.
 * @returns All log levels' loggers when needed.
 */
export function getLoggers(appName) {
  return {
    logDebug: getDebugLogger(appName),
    logError: getErrorLogger(appName),
    logInfo: getInfoLogger(appName),
    logWarning: getWarningLogger(appName)
  };
}
//# sourceMappingURL=log.js.map